import * as styles from './index.css';
import Blob from '@blueshift-ui/core/dist/components/blob';
import Button from '@blueshift-ui/core/dist/components/button';
import Container from '@blueshift-ui/core/dist/components/container';
import { Helmet } from 'react-helmet-async';
import Illustration from './assets/illustration.svg?react';
import RichTranslation from '@blueshift-ui/i18n/dist/components/rich-translation';
import Translation from '@blueshift-ui/i18n/dist/components/translation';
import Typography from '@blueshift-ui/theme/dist/components/typography';
import logHandledError from '@blueshift-ui/telemetry/dist/helpers/log-handled-error';
import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

interface ErrorPageProps {
  richContent?: boolean;
  variant: 'not_found' | 'oops';
}

function ErrorPage({ richContent = false, variant }: ErrorPageProps) {
  const { translate: richTranslate } = useTranslation(variant, { rich: true });
  const { translate } = useTranslation(variant);

  //Custom NR event to monitor an issue with Oops page
  const error = useRouteError();
  useEffect(() => {
    if (!error || variant !== 'oops') {
      return;
    }

    logHandledError(`[Oops]: An unexpected error occurred at ${window.location.href}`, {
      error,
      url: window.location.href,
    });
  }, [error, variant]);

  return (
    <>
      <Helmet>
        <title>Varsity Tutors | Client Account Error</title>
      </Helmet>
      <section className={styles.wrapper}>
        <Container className={styles.container} disableGutters maxWidth={false}>
          <Blob animated={true} componentProps={{ className: styles.blob }} />
          <Container className={styles.textContainer} disableGutters={true}>
            <Typography className={styles.title} component="h1" variant="h1">
              {translate('error')}
            </Typography>

            <Typography className={styles.subtitle} component="h2" variant="h6">
              {richContent ? (
                <RichTranslation
                  i18nKey="heading"
                  replace={{
                    components: {
                      br: <br />,
                    },
                  }}
                  translator={richTranslate}
                />
              ) : (
                <Translation
                  i18nKey="heading"
                  replace={{
                    components: {
                      br: <br />,
                    },
                  }}
                  translator={translate}
                />
              )}
            </Typography>

            <Typography className={styles.paragraph}>
              {richContent ? (
                <RichTranslation
                  i18nKey="paragraph"
                  replace={{
                    components: {
                      br: <br />,
                    },
                  }}
                  translator={richTranslate}
                />
              ) : (
                <Translation
                  i18nKey="paragraph"
                  replace={{
                    components: {
                      br: <br />,
                    },
                  }}
                  translator={translate}
                />
              )}{' '}
            </Typography>

            {translate('button_href') && (
              <Button className={styles.button} href={translate('button_href')}>
                {translate('button_text')}
              </Button>
            )}
          </Container>

          <Illustration className={styles.illustration} />
        </Container>
      </section>
    </>
  );
}

export default ErrorPage;
